/**
 * THE ONE MONEY BOX. Every place a family types an amount of money uses
 * `MoneyInput`: a bordered box with the currency code inside it on the left,
 * then the amount — "USD 0.00", the expense form's look (ZEN-815).
 *
 *   <MoneyInput currency={curCode(f.currency, "USD")} value={f.amount}
 *               onChange={(v) => set("amount", v)} testid="exp-amount" />
 *
 * For money that is only ever read: fmtAccounting(4000) → "4,000.00".
 *
 * WHY ONE. Screens that each decided privately what an amount looks like
 * drifted apart (ZEN-627: 91.8 shown under CAD 91.80; ZEN-815: "Amount (USD)"
 * on one form, "USD 0.00" on the next). `DollarInput` below is the bare typing
 * rules and is rendered only by `MoneyInput`; everyMoneyBoxIsTheSharedOne.test.js
 * holds every screen to that. The currency belongs in the box, not the label.
 */
import React, { useState } from "react";
import { curCode } from "@/lib/currencies";

/** Format a numeric value to accounting style: 2 decimals + thousands separators. */
export function fmtAccounting(v) {
  if (v === "" || v == null) return "";
  const n = typeof v === "number" ? v : Number(v);
  if (isNaN(n)) return String(v);
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// WHAT MAY BE TYPED INTO A MONEY BOX.
//
// Digits, at most one decimal point, and AT MOST TWO DIGITS AFTER IT. The split
// dialog's own box already refused a third decimal and nothing else did; the
// alternative is accepting 33.335 and silently settling it to 33.34, which on a
// tax record is the wrong kind of helpful. Refused at the keystroke, the box
// visibly does nothing — and "nothing happened" is a state a person can see and
// correct, unlike a figure that quietly changed.
//
// NO LEADING MINUS: an amount in this product is money that moved, and money
// that moved the other way is its own record pointing at what it reverses — the
// owner's ruling of 2026-09-01, already enforced by the server and enforced by
// hand in the expense dialog's own box before this file absorbed it.
const TYPEABLE = /^\d*\.?\d{0,2}$/;
// The one exception: 1099-Q box 2 carries a final-year loss as a minus figure
// (Maryse, 28 Oct 2026, ZEN-823). Only a box that asks for it gets it.
const TYPEABLE_SIGNED = /^-?\d*\.?\d{0,2}$/;

// AND NO COMMA AT ALL, WHICH IS THE THIRD ANSWER AND THE ONLY SAFE ONE.
//
// A comma is not decidable from the string it sits in. Two attempts at deciding
// it are recorded here because both looked obviously right and both were a way
// to change a tax figure without anybody seeing it happen:
//
//   1. Strip every comma. On a keyboard where the comma IS the decimal point
//      "91,80" becomes 9180 — a hundred times too much — and a pasted
//      "1.234,56" becomes 1.23.
//   2. Strip a comma only from well-formed en-US grouping, three digits behind
//      each one. That still accepts "33,335", which is thirty-three thousand in
//      one convention and thirty-three point three three five in another. A
//      THOUSAND times too much, found by the adversarial reader, not by me.
//
// So a comma is simply refused, which is also exactly what every one of these
// boxes did before they were folded together. The cost is that a figure this
// box printed cannot be pasted straight back into it — the box visibly does
// nothing, and a person can see that and retype. The alternative is a figure
// that quietly became a different figure.
//
// Do not add a third attempt. Add a currency-aware parser or leave it alone.

/** The value a keystroke should leave in state, or null to ignore the keystroke. */
function accept(next, allowNegative = false) {
  const v = String(next);
  return v === "" || (allowNegative ? TYPEABLE_SIGNED : TYPEABLE).test(v) ? v : null;
}

/** What a money box holds, as the server wants it: empty is null, 0 stays 0. (ZEN-823) */
export function typedFigure(v) {
  return v === "" || v == null ? null : Number(v);
}

/**
 * Money input with format-on-blur.
 *
 * While the box has focus it shows exactly what was typed, so editing is never
 * fought by a reformatter mid-keystroke. On leaving, the value is settled to two
 * decimals and shown with thousands separators.
 *
 * `onChange` is handed the raw string — not an event — because every caller
 * wants the value, and several derive other fields from it.
 *
 * Any other attribute (min, max, disabled, aria-*, onKeyDown…) is passed
 * straight to the input element.
 */
export function DollarInput({ value, onChange, placeholder, testid, className = "", allowNegative = false, ...rest }) {
  const [focused, setFocused] = useState(false);

  const display = focused ? (value ?? "") : fmtAccounting(value);

  const handleChange = (e) => {
    const v = accept(e.target.value, allowNegative);
    if (v !== null) onChange(v);
  };

  const handleBlur = (e) => {
    setFocused(false);
    if (value !== "" && value != null) {
      const n = parseFloat(value);
      // A LONE "." IS NOT AN AMOUNT, and it used to survive: parseFloat(".") is
      // NaN so the old blur left it alone, and every "did they fill this in?"
      // check in the product reads a non-empty string as filled. Emptied here,
      // so an unfinished box looks unfinished.
      onChange(isNaN(n) ? "" : n.toFixed(2));
    }
    rest.onBlur?.(e);
  };

  // `type` and `inputMode` are written AFTER the spread deliberately, so a
  // caller cannot hand this box a type of its own. "number" in particular would
  // re-open the very hole this file closes: a number box cannot be shown with
  // thousands separators at all, and the browser decides its own formatting.
  return (
    <input
      {...rest}
      type="text"
      inputMode="decimal"
      value={display}
      onFocus={(e) => { setFocused(true); rest.onFocus?.(e); }}
      onBlur={handleBlur}
      onChange={handleChange}
      placeholder={placeholder}
      data-testid={testid}
      className={className}
    />
  );
}

const HEIGHT = { md: "h-10", sm: "h-8" };

/**
 * The money box every screen uses: currency code, then the amount.
 *
 * `currency` is shown exactly as given — pass the code the screen's own
 * currency picker shows, so the box never claims a currency the record lacks.
 * `size="sm"` is for boxes inside a row or panel; `warning` gives the amber
 * "please check this" border; `className` sizes the outer box (a width, a
 * margin). Anything else goes to the input element.
 */
export function MoneyInput({ currency, value, onChange, testid, placeholder = "0.00",
  size = "md", warning = false, className = "", ...rest }) {
  const sm = size === "sm";
  const border = warning ? "border-[#D97706]" : "border-[#C2C9D1]";
  return (
    <div className={`flex ${HEIGHT[size] || HEIGHT.md} w-full min-w-0 items-center rounded-md border ${border} ${rest.disabled ? "bg-[#E9E9EB]" : "bg-white"} focus-within:border-[#034AA2] focus-within:ring-2 focus-within:ring-[#034AA2]/30 ${className}`}>
      <span className={`shrink-0 select-none font-medium text-[#6b7280] ${sm ? "pl-2 pr-1 text-xs" : "pl-3 pr-1.5 text-sm"}`}
        data-testid={testid ? `${testid}-currency` : undefined}>{curCode(currency)}</span>
      <DollarInput {...rest} value={value} onChange={onChange} placeholder={placeholder} testid={testid}
        className={`h-full w-full min-w-0 bg-transparent text-[#0A0A0A] outline-none ${sm ? "pr-2 text-xs" : "pr-3 text-sm"}`} />
    </div>
  );
}
